import { request } from '@umijs/max';
import type * as Types from './types';

const PROJECT_ARRAY_PARAM_SINGLE_MAP: Partial<
  Record<keyof Types.ProjectFetchRequest, keyof Types.ProjectFetchRequest>
> = {
  statuses: 'status',
  adStatuses: 'adStatus',
  appPlatforms: 'appPlatform',
  departments: 'department',
  domainInfoStatuses: 'domainInfoStatus',
  facebookInfoStatuses: 'facebookInfoStatus',
  admobAccountStatuses: 'admobAccountStatus',
  googlePlayVersionStatuses: 'googlePlayVersionStatus',
  ownerIds: 'ownerId',
};

const projectArrayParamKeys = new Set<string>(
  Object.keys(PROJECT_ARRAY_PARAM_SINGLE_MAP),
);

const toProjectParamList = (value: unknown) => {
  const rows = Array.isArray(value)
    ? value
    : value !== undefined && value !== null
      ? [value]
      : [];
  return rows
    .map((item) => `${item}`.trim())
    .filter(Boolean);
};

const appendProjectParam = (
  searchParams: URLSearchParams,
  key: string,
  value: unknown,
) => {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    value.forEach((item) => appendProjectParam(searchParams, `${key}[]`, item));
    return;
  }
  const text = `${value}`.trim();
  if (text) {
    searchParams.append(key, text);
  }
};

const serializeProjectFetchParams = (query: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (projectArrayParamKeys.has(key)) return;
    appendProjectParam(searchParams, key, value);
  });

  Object.entries(PROJECT_ARRAY_PARAM_SINGLE_MAP).forEach(
    ([arrayKey, singleKey]) => {
      const values = toProjectParamList(query[arrayKey]);
      values.forEach((value) => searchParams.append(`${arrayKey}[]`, value));

      const singleValue = query[singleKey];
      if (
        (singleValue === undefined ||
          singleValue === null ||
          singleValue === '') &&
        values.length
      ) {
        searchParams.append(singleKey, values[0]);
      }
    },
  );

  return searchParams.toString();
};

// Projects
export async function getProjects(params?: Types.ProjectFetchRequest) {
  return request<API.ApiResponse<API.PageResult<Types.ProjectItem>>>('/v3/projects/', {
    method: 'GET',
    params,
    paramsSerializer: serializeProjectFetchParams,
  });
}

export async function getProjectDetail(id: number) {
  return request<API.ApiResponse<Types.ProjectItem>>(`/v3/projects/detail`, {
    method: 'GET',
    params: { id },
  });
}

export async function createProject(data: Types.ProjectStoreRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/create', {
    method: 'POST',
    data,
  });
}

export async function updateProject(data: Types.ProjectUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/update', {
    method: 'POST',
    data,
  });
}

export async function updateProjectStatus(data: Types.ProjectStatusUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/update-status', {
    method: 'POST',
    data,
  });
}

export async function batchUpdateProjectAdStatus(data: Types.ProjectBatchUpdateAdStatusRequest) {
  return request<API.ApiResponse<Types.ProjectBatchUpdateAdStatusResult>>('/v3/projects/batch-update-ad-status', {
    method: 'POST',
    data,
  });
}

export async function batchUpdateProjectAppPlatform(data: Types.ProjectBatchUpdateAppPlatformRequest) {
  return request<API.ApiResponse<Types.ProjectBatchUpdateAppPlatformResult>>('/v3/projects/batch-update-app-platform', {
    method: 'POST',
    data,
  });
}

export async function batchUpdateProjectDepartment(data: Types.ProjectBatchUpdateDepartmentRequest) {
  return request<API.ApiResponse<Types.ProjectBatchUpdateDepartmentResult>>('/v3/projects/batch-update-department', {
    method: 'POST',
    data,
  });
}

export async function getProjectCodes() {
  return request<API.ApiResponse<Types.ProjectCodeListResult>>('/v3/projects/project-codes', {
    method: 'GET',
  });
}

export async function getProjectDepartments() {
  return request<API.ApiResponse<Types.ProjectDepartmentListResult>>('/v3/projects/departments', {
    method: 'GET',
  });
}

export async function batchSaveProjects(data: Types.ProjectBatchSaveRequest) {
  return request<API.ApiResponse<Types.ProjectBatchSaveResult>>('/v3/projects/batch-save', {
    method: 'POST',
    data,
  });
}

// Traffic Accounts
export async function getTrafficAccounts(projectId: number) {
  return request<API.ApiResponse<{ data: Types.ProjectTrafficAccount[] }>>('/v3/projects/traffic-accounts', {
    method: 'GET',
    params: { project_id: projectId },
  });
}

export async function createTrafficAccount(data: Types.ProjectTrafficAccountStoreRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/traffic-accounts/create', {
    method: 'POST',
    data,
  });
}

export async function updateTrafficAccount(data: Types.ProjectTrafficAccountUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/traffic-accounts/update', {
    method: 'POST',
    data,
  });
}

export async function deleteTrafficAccount(data: Types.ProjectResourceIdRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/traffic-accounts/delete', {
    method: 'POST',
    data,
  });
}

// Ad Accounts
export async function getAdAccounts(projectId: number) {
  return request<API.ApiResponse<{ data: Types.ProjectAdAccount[] }>>('/v3/projects/ad-accounts', {
    method: 'GET',
    params: { project_id: projectId },
  });
}

export async function createAdAccount(data: Types.ProjectAdAccountStoreRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/ad-accounts/create', {
    method: 'POST',
    data,
  });
}

export async function updateAdAccount(data: Types.ProjectAdAccountUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/ad-accounts/update', {
    method: 'POST',
    data,
  });
}

export async function deleteAdAccount(data: Types.ProjectResourceIdRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/ad-accounts/delete', {
    method: 'POST',
    data,
  });
}

// User Apps
export async function getUserApps(projectId: number) {
  return request<API.ApiResponse<{ data: Types.ProjectUserApp[] }>>('/v3/projects/user-apps', {
    method: 'GET',
    params: { project_id: projectId },
  });
}

export async function getUserAppMappings() {
  return request<API.ApiResponse<Types.ProjectUserAppMapping[]>>('/v3/projects/user-apps/mappings', {
    method: 'GET',
  });
}

export async function createUserApp(data: Types.ProjectUserAppStoreRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/user-apps/create', {
    method: 'POST',
    data,
  });
}

export async function updateUserApp(data: Types.ProjectUserAppUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/user-apps/update', {
    method: 'POST',
    data,
  });
}

export async function deleteUserApp(data: Types.ProjectResourceIdRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/user-apps/delete', {
    method: 'POST',
    data,
  });
}

// Mailboxes
export async function getProjectMailboxes(params?: Types.ProjectMailboxFetchRequest) {
  return request<API.ApiResponse<API.PageResult<Types.ProjectMailbox>>>('/v3/projects/mailboxes', {
    method: 'GET',
    params,
  });
}

export async function createProjectMailbox(data: Types.ProjectMailboxStoreRequest) {
  return request<API.ApiResponse<Types.ProjectMailbox>>('/v3/projects/mailboxes/create', {
    method: 'POST',
    data,
  });
}

export async function updateProjectMailbox(data: Types.ProjectMailboxUpdateRequest) {
  return request<API.ApiResponse<Types.ProjectMailbox>>('/v3/projects/mailboxes/update', {
    method: 'POST',
    data,
  });
}

export async function deleteProjectMailbox(data: Types.ProjectMailboxDeleteRequest) {
  return request<API.ApiResponse<boolean>>('/v3/projects/mailboxes/delete', {
    method: 'POST',
    data,
  });
}

// Mail Messages
export async function getProjectMailMessages(params?: Types.ProjectMailMessageFetchRequest) {
  return request<API.ApiResponse<API.PageResult<Types.ProjectMailMessage>>>('/v3/projects/mail-messages', {
    method: 'GET',
    params,
  });
}

export async function getProjectMailMessageDetail(id: number) {
  return request<API.ApiResponse<Types.ProjectMailMessage>>('/v3/projects/mail-messages/detail', {
    method: 'GET',
    params: { id },
  });
}

export async function updateProjectMailMessageStatus(data: Types.ProjectMailMessageStatusRequest) {
  return request<API.ApiResponse<Types.ProjectMailMessage>>('/v3/projects/mail-messages/update-status', {
    method: 'POST',
    data,
  });
}

// Version Records
export async function getProjectVersionRecords(params?: Types.ProjectVersionRecordFetchRequest) {
  return request<API.ApiResponse<Types.ProjectVersionRecordPageResult>>('/v3/projects/version-records', {
    method: 'GET',
    params,
  });
}

export async function createProjectVersionRecord(data: Types.ProjectVersionRecordStoreRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/version-records/create', {
    method: 'POST',
    data,
  });
}

export async function publishProjectVersionRecord(data: {
  projectId: number;
  packageType: Types.ProjectPackageType;
  version: string;
  versionName?: string | null;
  packageName: string;
  storePageUrl: string;
  content: string;
  releaseTime: string;
  remark?: string | null;
}) {
  return request<API.ApiResponse<any>>('/v3/projects/version-records/publish', {
    method: 'POST',
    data,
  });
}

export async function getProjectWorkbench(
  type: 'developer' | 'product',
  params?: {
    keyword?: string;
    memberId?: number;
    adStatus?: string;
    progressStatus?: Types.ProjectDevelopmentProgressStatus;
    taskScope?: Types.ProjectTaskScope;
    dateFrom?: string;
    dateTo?: string;
  },
) {
  return request<API.ApiResponse<Types.ProjectWorkbenchResult>>(`/v3/projects/workbench/${type}`, {
    method: 'GET',
    params,
  });
}

export async function updateProjectDevelopmentProgress(data: {
  projectId: number;
  progress: number;
  content?: string;
}) {
  return request<API.ApiResponse<{ projectId: number; progress: number }>>(
    '/v3/projects/workbench/development-progress',
    { method: 'POST', data },
  );
}

export async function getProjectWorkflowOptions() {
  return request<API.ApiResponse<Types.ProjectWorkflowOptions>>('/v3/projects/workflow/options', {
    method: 'GET',
  });
}

export async function createProjectRequirement(data: {
  projectId?: number;
  projectIds?: number[];
  assigneeIds?: number[];
  category: Types.ProjectIssueType;
  priority?: Types.ProjectTaskPriority;
  taskAssignmentType?: Types.ProjectTaskAssignmentType;
  acceptancePoint?: Types.ProjectAcceptancePoint | string;
  acceptanceRemark?: string;
  title?: string;
  content: string;
  dispatchDate?: string;
  dueDate?: string;
  attachments?: Types.ProjectTaskAttachment[];
}) {
  return request<API.ApiResponse<any>>('/v3/projects/tasks/requirements/create', { method: 'POST', data });
}

export async function getProjectAccountOpenings(params?: {
  keyword?: string;
  productAbbreviation?: string;
  productName?: string;
  platform?: string;
  storeUrl?: string;
  adStatus?: string;
  onlineDateStart?: string;
  onlineDateEnd?: string;
  productStatus?: string;
  mediaBuyer?: string;
  facebookAppId?: string;
  developerEmail?: string;
  primaryCategory?: string;
  secondaryCategory?: string;
  monetization?: string;
  thirdPartyIntegration?: string;
  discountCallbackStatus?: string;
  filterCallbackStatus?: string;
  remark?: string;
  page?: number;
  pageSize?: number;
}) {
  return request<API.ApiResponse<{ items: Types.ProjectAccountOpening[]; total: number; page: number; pageSize: number }>>('/v3/projects/account-openings', { method: 'GET', params });
}

export async function updateProjectAccountOpening(id: number, data: Partial<Types.ProjectAccountOpening>) {
  return request<API.ApiResponse<Types.ProjectAccountOpening>>(`/v3/projects/account-openings/${id}/update`, { method: 'POST', data });
}

export async function getProjectAccountRemarks(id: number) {
  return request<API.ApiResponse<Types.ProjectAccountRemark[]>>(`/v3/projects/account-openings/${id}/remarks`, { method: 'GET' });
}

export async function addProjectAccountRemark(id: number, data: { content: string; mentionedUserIds: number[] }) {
  return request<API.ApiResponse<Types.ProjectAccountRemark>>(`/v3/projects/account-openings/${id}/remarks`, { method: 'POST', data });
}

export async function getProjectAccountNotifications() {
  return request<API.ApiResponse<Types.ProjectAccountNotification[]>>('/v3/projects/account-opening-notifications', { method: 'GET' });
}

export async function readProjectAccountNotification(id: number) {
  return request<API.ApiResponse<boolean>>(`/v3/projects/account-opening-notifications/${id}/read`, { method: 'POST' });
}

export async function getProjectTaskNotifications() {
  return request<API.ApiResponse<Types.ProjectTaskNotification[]>>('/v3/projects/notifications', { method: 'GET' });
}

export async function submitProjectTaskFeedback(data: {
  taskId: number;
  feedbackType?: 'version' | 'issue' | 'requirement' | 'progress';
  issueType?: Types.ProjectIssueType;
  content: string;
  attachments?: Types.ProjectTaskAttachment[];
  currentBlocker?: string;
  delayReason?: string;
  acceptanceRemark?: string;
  acceptanceScore?: number;
  acceptanceEvaluation?: string;
  dueDate?: string;
  expectedResolutionDate?: string;
  productSupportRequired?: boolean;
  status?: Types.ProjectTaskStatus;
}) {
  return request<API.ApiResponse<any>>('/v3/projects/tasks/feedback', { method: 'POST', data });
}

export async function remindProjectTaskAcceptance(data: { taskId: number }) {
  return request<API.ApiResponse<Types.ProjectTaskNotification>>(
    '/v3/projects/tasks/remind-acceptance',
    { method: 'POST', data },
  );
}

export async function readProjectNotification(id: number) {
  return request<API.ApiResponse<any>>('/v3/projects/notifications/read', {
    method: 'POST',
    data: { id },
  });
}

export async function batchCreateProjectVersionRecords(data: Types.ProjectVersionRecordBatchCreateRequest) {
  return request<API.ApiResponse<Types.ProjectVersionRecordBatchCreateResult>>(
    '/v3/projects/version-records/batch-create',
    {
      method: 'POST',
      data,
    },
  );
}

export async function updateProjectVersionRecord(data: Types.ProjectVersionRecordUpdateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/version-records/update', {
    method: 'POST',
    data,
  });
}

export async function deleteProjectVersionRecord(data: Types.ProjectVersionRecordDeleteRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/version-records/delete', {
    method: 'POST',
    data,
  });
}

// Aggregation
export async function aggregateSync(data: Types.AggregateRequest) {
  return request<API.ApiResponse<any>>('/v3/projects/aggregate', {
    method: 'POST',
    data,
  });
}

export async function aggregateAsync(data: Types.AggregateRequest) {
  // Returns accepted etc directly or wrapped? Based on doc: returns the JSON
  return request<{ accepted: boolean; triggerId: string; startDate: string; endDate: string; status: string }>('/v3/projects/aggregate-async', {
    method: 'POST',
    data,
  });
}

export async function aggregateHourly(data: Types.AggregateHourlyRequest) {
  return request<API.ApiResponse<Types.AggregateHourlyResult>>('/v3/projects/aggregate-hourly', {
    method: 'POST',
    data,
  });
}
